/**
 * CommitmentCell — how a commitment is quoted anywhere outside its own page.
 *
 * PRD: docs/9-exploration/DECISION-PROPAGATION-AND-LINEAGE-PRD.md §5–§6.
 *
 * Two jobs, both of them the point of the phase:
 *   * P3 — "every other surface links to it". A commitment named in a table is
 *     a dead end today; here it is a link to the canonical view, so the reader
 *     who wants to know why is one click away rather than in a meeting.
 *   * P4 — the version it is quoting, and a marker when that version is no
 *     longer the authorized one.
 *
 * Degrades honestly in both directions. With the module off it renders exactly
 * the plain label the surface rendered before, and with no version information
 * it shows the name alone rather than a marker asserting something it does not
 * know.
 */

import { Link } from 'react-router-dom';
import { CommitmentStaleMarker } from './CommitmentStaleMarker';

export interface CommitmentCellProps {
  planCycleId: string;
  itemId: string | null;
  /** The label this surface already shows for the item. */
  label: string;
  /** True when the commitment_view module is on for this record's workspace. */
  linkEnabled: boolean;
  /** The plan version this row was written against, if the row records one. */
  quotedVersionNumber?: number | null;
  /** The cycle's active authorized version. */
  authoritativeVersionNumber?: number | null;
  className?: string;
}

export function CommitmentCell({
  planCycleId,
  itemId,
  label,
  linkEnabled,
  quotedVersionNumber,
  authoritativeVersionNumber,
  className = '',
}: CommitmentCellProps) {
  const text = <span className="text-sm text-[var(--text-pri)]">{label}</span>;

  // A row with no item cannot address a commitment; a disabled module must
  // leave the surface byte-identical to what it rendered before.
  const body =
    itemId && linkEnabled ? (
      <Link
        to={`/planning/plans/${planCycleId}/commitments/${itemId}`}
        className="text-sm text-[var(--text-pri)] underline decoration-[var(--rule)] underline-offset-2 hover:decoration-[var(--text-sec)]"
      >
        {label}
      </Link>
    ) : (
      text
    );

  // Only claim drift when both numbers are known. Rendering "version unknown"
  // on a surface that simply does not record versions would be noise on every
  // row, and a marker people learn to ignore is not a marker.
  const showMarker =
    quotedVersionNumber !== undefined &&
    quotedVersionNumber !== null &&
    authoritativeVersionNumber !== undefined &&
    authoritativeVersionNumber !== null;

  return (
    <span className={`inline-flex flex-wrap items-center gap-2 ${className}`}>
      {body}
      {showMarker && (
        <CommitmentStaleMarker
          quotedVersionNumber={quotedVersionNumber}
          authoritativeVersionNumber={authoritativeVersionNumber}
          hideWhenCurrent
        />
      )}
    </span>
  );
}

export default CommitmentCell;
